import { c as _c } from "react/compiler-runtime";
import { type CSSProperties, type ReactNode, type SyntheticEvent, useState } from 'react';
import HLSVideo from '@/components/HLSVideo.js';
import Modal from '../Modal';
import styles from './VideoModal.module.scss';

/**
 * Where a VideoModal's `src` plays (read from the URL, as the legacy
 * IntroductionModal did): `file` an HLS manifest or a video file in a native
 * player (HLSVideo); `youtube` a YouTube video in a `srcdoc` frame; `embed`
 * any other URL, a player page (Mux, Vimeo) in an iframe.
 */
export type VideoModalSource = 'file' | 'embed' | 'youtube';
export interface VideoModalProps {
  /** Controls visibility (Modal's open). */
  open: boolean;
  /** Called by the close control, Escape and a click outside the frame. */
  onClose: () => void;
  /**
   * The video's name: the dialog's accessible name, the iframe's title and
   * the native player's label; the close control is "Close <label>".
   * Required.
   */
  label: string;
  /**
   * An HLS manifest (.m3u8) or a video file (.mp4, .webm); a YouTube URL
   * (an /embed/ URL plays exactly as given; youtu.be, watch?v=, /shorts/ and
   * /live/ play on the privacy-enhanced embed); or a player page URL, which
   * plays in an iframe as given (put autoplay in it, as Mux's `?autoplay`).
   */
  src: string;
  /**
   * Start time in seconds, for a file or a YouTube video (a YouTube URL with
   * a start plays on the privacy-enhanced embed). A player page starts where
   * its own URL says.
   */
  start?: number;
  /** A poster for a file video. */
  poster?: string;
}

// The iframe allow lists IntroductionModal used, unchanged: a player page
// may autoplay; the YouTube frame gets the YouTube embed's own list.
const EMBED_ALLOW = 'autoplay';
const YOUTUBE_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
const FILE_SOURCE = /\.(m3u8|mp4|webm)(?:$|[?#])/i;
const YOUTUBE_HOSTS = ['youtube.com', 'youtube-nocookie.com', 'youtu.be'];

// Decided on the parsed hostname, so a YouTube name inside another host's
// path or query (`https://example.com/youtube.com/...`) stays an ordinary
// embed.
function isYoutubeHost(src: string) {
  let hostname: string;
  try {
    hostname = new URL(src).hostname.toLowerCase();
  } catch {
    return false;
  }
  return YOUTUBE_HOSTS.some(host => hostname === host || hostname.endsWith(`.${host}`));
}
export function videoModalSource(src: string): VideoModalSource {
  if (isYoutubeHost(src)) return 'youtube';
  if (FILE_SOURCE.test(src)) return 'file';
  return 'embed';
}

/** The video id of a YouTube URL, or null when the URL names none. */
export function youtubeVideoId(src: string) {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  const id = url.hostname === 'youtu.be' || url.hostname.endsWith('.youtu.be') ? url.pathname.slice(1) : url.searchParams.get('v') ?? url.pathname.match(/^\/(?:embed|shorts|live)\/([^/]+)/)?.[1] ?? null;
  return id && /^[\w-]{6,}$/.test(id) ? id : null;
}
function isYoutubeEmbedUrl(src: string) {
  try {
    return new URL(src).pathname.startsWith('/embed/');
  } catch {
    return false;
  }
}

/**
 * The URL the YouTube frame plays. A caller's /embed/ URL with no start plays
 * exactly as given (host, parameters and all), as the legacy
 * IntroductionModal framed it: the ASHCA hero's video is one. Any other URL
 * with a video id (youtu.be, watch?v=, /shorts/, /live/), or any with a
 * start, plays on the privacy-enhanced embed with autoplay, the start and
 * YouTube's default captions (`cc_load_policy=0` leaves them to the viewer's
 * setting: YouTube honours only 1, captions on; `modestbranding` has had no
 * effect since 2023). A URL with no video id plays as given.
 */
export function youtubeEmbedSrc(src: string, start = 0) {
  const id = youtubeVideoId(src);
  if (!id) return src;
  const startSeconds = Math.max(0, Math.floor(start));
  if (!startSeconds && isYoutubeEmbedUrl(src)) return src;
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&cc_load_policy=0&controls=1&modestbranding=1&playsinline=1&rel=0${startSeconds ? `&start=${startSeconds}` : ''}`;
}
function escapeAttribute(value: string) {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

// The YouTube player sits in a srcdoc document of its own, so it gets no
// page referrer beyond the origin and its frame is black before it paints.
// That document cannot read the page's custom properties: its black is the
// literal of the frame's surface-dark.
function youtubeSrcDoc(src: string, label: string) {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      html, body, iframe {
        width: 100%;
        height: 100%;
        margin: 0;
        border: 0;
        background: #000;
      }
    </style>
  </head>
  <body>
    <iframe
      src="${escapeAttribute(src)}"
      title="${escapeAttribute(label)}"
      allow="${YOUTUBE_ALLOW}"
      allowfullscreen
      referrerpolicy="strict-origin-when-cross-origin"
    ></iframe>
  </body>
</html>`;
}

/**
 * A video in a dialog (the VideoModal recipe, Phase 4 PR 10; frameless since
 * Phase 6b): Modal `size="video"` over the blurred backdrop, the video alone
 * (no panel, bar or border; its corners at radius xl) sized to its ratio,
 * and the close above its top-right corner, clear of the player's own
 * controls. The player is chosen from `src`; the frame is 16:9, or a file's
 * own ratio once its metadata loads.
 */
function VideoModal(t0) {
  const $ = _c(36);
  const {
    open,
    onClose,
    label,
    src,
    start,
    poster
  } = t0;
  let t1;
  if ($[0] !== src) {
    t1 = videoModalSource(src);
    $[0] = src;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const source = t1;
  const [fileRatio, setFileRatio] = useState(null);
  const ratio = source === "file" && fileRatio?.src === src ? fileRatio.ratio : null;
  let t2;
  if ($[2] !== src || $[3] !== start) {
    t2 = event => {
      const video = event.currentTarget;
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setFileRatio({
          src,
          ratio: video.videoWidth / video.videoHeight
        });
      }
      if (!start || start <= 0 || video.currentTime > 0) {
        return;
      }
      const end = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.1) : start;
      video.currentTime = Math.min(start, end);
    };
    $[2] = src;
    $[3] = start;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onLoadedMetadata = t2;
  let player;
  if (source === "file") {
    let t3;
    if ($[5] !== label || $[6] !== onLoadedMetadata || $[7] !== poster || $[8] !== src) {
      t3 = <HLSVideo aria-label={label} autoPlay={true} className={styles["helios-video-modal__media"]} controls={true} onLoadedMetadata={onLoadedMetadata} playsInline={true} poster={poster} src={src} />;
      $[5] = label;
      $[6] = onLoadedMetadata;
      $[7] = poster;
      $[8] = src;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    player = t3;
  } else {
    if (source === "youtube") {
      let t3;
      if ($[10] !== label || $[11] !== src || $[12] !== start) {
        t3 = youtubeSrcDoc(youtubeEmbedSrc(src, start), label);
        $[10] = label;
        $[11] = src;
        $[12] = start;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      let t4;
      if ($[14] !== label || $[15] !== t3) {
        t4 = <iframe allow={YOUTUBE_ALLOW} allowFullScreen={true} className={styles["helios-video-modal__media"]} srcDoc={t3} title={label} />;
        $[14] = label;
        $[15] = t3;
        $[16] = t4;
      } else {
        t4 = $[16];
      }
      player = t4;
    } else {
      let t3;
      if ($[17] !== label || $[18] !== src) {
        t3 = <iframe allow={EMBED_ALLOW} allowFullScreen={true} className={styles["helios-video-modal__media"]} src={src} title={label} />;
        $[17] = label;
        $[18] = src;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      player = t3;
    }
  }
  let t3;
  if ($[20] !== ratio) {
    t3 = ratio ? {
      "--helios-video-modal-ratio": Number(ratio.toFixed(4))
    } as CSSProperties : undefined;
    $[20] = ratio;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  const t4 = `Close ${label}`;
  let t5;
  if ($[22] !== t4) {
    t5 = <div className={styles["helios-video-modal__close"]}><Modal.Close aria-label={t4} /></div>;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== player) {
    t6 = <div className={styles["helios-video-modal__player"]}>{player}</div>;
    $[24] = player;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== source || $[27] !== t3 || $[28] !== t5 || $[29] !== t6) {
    t7 = <Modal.Body padding="none"><div className={styles["helios-video-modal"]} data-helios-video-modal="" data-source={source} style={t3}>{t5}{t6}</div></Modal.Body>;
    $[26] = source;
    $[27] = t3;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== label || $[32] !== onClose || $[33] !== open || $[34] !== t7) {
    t8 = <Modal ariaLabel={label} backdrop="blurred" onClose={onClose} open={open} size="video">{t7}</Modal>;
    $[31] = label;
    $[32] = onClose;
    $[33] = open;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}
export default VideoModal;